import { ErrorAlert } from "../../../../utils";
import { TextField } from "../../../../utils/form";
import { Button, Stack } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { useForm } from "react-hook-form";
import CustomDialog from "../../../../utils/CustomDialog";
import RadioGroup from "../../../../utils/form/RadioGrup";
import {
  templateTypes,
  templateMaxItems,
  getDefaultValues,
} from "./utils/constants";
import NomenclatureSearch from "../search/NomenclatureSearch";
import {
  contentMapper,
  getTemplateCreateType,
  getTemplateCreationErrorMessage,
  NameHelperText,
  extractItems,
  exceedsMaxItems,
  setMaxItemsErrorMessage,
  hasReachedMaxItems,
  procedureTypeFromTemplateType,
  capForProcedureType,
  hasReachedMaxItemsForType,
  getExceededProcedureType,
  resolveItemProcedureType,
} from "./utils";
import NomenclatureOrderItem from "../nomenclatureOrder/NomenclatureOrderItem";
import { OrderType } from "../search/utils/constants";

const TemplateDialog = ({
  originalTemplate,
  open,
  toggleOpen,
  setSelectedTemplate,
  defaultContent,
  onSubmit,
  title,
  buttonText,
  orderType,
  isNomenclatureV3,
}) => {
  const isPractices = orderType === OrderType.PRACTICES;
  // DEV-1687: única condición que habilita mezclar imaging y laboratory en una
  // misma plantilla — evita repetir `isPractices && isNomenclatureV3` (con
  // distinto orden de operandos) en cada punto donde importa.
  const supportsMixedTemplate = isPractices && isNomenclatureV3;

  const defaultValues = originalTemplate
    ? _.pick(originalTemplate, "name", "type", "content", "inputMethod")
    : getDefaultValues(defaultContent);

  const originalTemplateProcedureType =
    originalTemplate?.procedureType ??
    procedureTypeFromTemplateType(originalTemplate?.type) ??
    null;

  const [selectedItems, setSelectedItems] = useState([
    ...extractItems(
      originalTemplate,
      isPractices ? (originalTemplateProcedureType ?? OrderType.IMAGING) : orderType
    ),
  ]);
  const [method, setMethod] = useState(defaultValues.inputMethod);
  const [error, setError] = useState();

  /**
   * In PRACTICES mode: null until the first item is added, then locked to that item's
   * procedureType — this is what limits a template to a single type (imaging OR
   * laboratory). DEV-1687: v3 supports mixed templates, so the lock never engages there
   * (items keep their own procedureType, applied later grouped per type — see
   * groupItemsByProcedureType in web-client/src/modules/homologar/orders/utils/orders.js).
   */
  const [lockedProcedureType, setLockedProcedureType] = useState(
    isPractices && !supportsMixedTemplate ? originalTemplateProcedureType : null
  );

  // DEV-1687: en v3 el tipo no se resuelve a uno solo (la plantilla puede ser
  // mixta) — sólo hace falta un valor válido para el content mapper / tipo de
  // creación, y PRACTICES ya se trata igual que imaging/laboratory en ambos.
  const resolvedOrderType = isPractices
    ? supportsMixedTemplate
      ? OrderType.PRACTICES
      : (lockedProcedureType ?? OrderType.IMAGING)
    : orderType;

  // Sin tipo único, el límite de `templateMaxItems` (laboratorio sin límite,
  // imaging con tope) se evalúa por tipo en vez de con un único `maxItems` — ver
  // hasReachedMaxItemsForType / getExceededProcedureType más abajo. `null` acá
  // desactiva el chequeo agregado (hasReachedMaxItems/exceedsMaxItems con `null`
  // siempre da `false`).
  const maxItems = supportsMixedTemplate ? null : templateMaxItems[method][resolvedOrderType];

  // procedureId es una identidad estable; name puede repetirse por formato
  // (espacios, mayúsculas) entre dos selecciones del mismo ítem del buscador.
  const itemKey = (item) => item.procedureId ?? item.name;

  /**
   * { name: string, procedureId?: number, procedureType?: string }[]
   */
  const addItem = (item) => {
    if (!item || !item.name) {
      return;
    }
    if (selectedItems.some((i) => itemKey(i) === itemKey(item))) {
      setError("No se puede agregar la misma práctica más de una vez.");
      return;
    }

    if (supportsMixedTemplate) {
      const itemProcedureType = resolveItemProcedureType(item);
      if (hasReachedMaxItemsForType(method, itemProcedureType, selectedItems)) {
        setMaxItemsErrorMessage(capForProcedureType(method, itemProcedureType, selectedItems), setError);
        return;
      }
    }

    if (isPractices && !supportsMixedTemplate && lockedProcedureType === null && item.procedureType) {
      setLockedProcedureType(item.procedureType);
    }

    setSelectedItems((prev) => [...prev, item]);
  };

  const onItemRemove = (item) => {
    setSelectedItems((prev) => {
      const next = prev.filter((i) => itemKey(i) !== itemKey(item));
      if (isPractices && next.length === 0) {
        setLockedProcedureType(null);
      }
      return next;
    });
  };

  const form = useForm({ defaultValues });
  const { handleSubmit, control } = form;

  const onClose = () => {
    toggleOpen();
    setSelectedItems([]);
    if (isPractices) setLockedProcedureType(null);
    form.reset();
  };

  const _onSubmit = (templateDto) => {
    if (selectedItems.length === 0) {
      setError("Debes seleccionar al menos una práctica");
      return;
    }

    if (supportsMixedTemplate) {
      const exceededProcedureType = getExceededProcedureType(method, selectedItems);
      if (exceededProcedureType) {
        setMaxItemsErrorMessage(capForProcedureType(method, exceededProcedureType, selectedItems), setError);
        return;
      }
    } else if (exceedsMaxItems(maxItems, selectedItems)) {
      setMaxItemsErrorMessage(maxItems, setError);
      return;
    }

    const fullDto = {
      ...templateDto,
      ...contentMapper[resolvedOrderType][method].mapItemsToContent(selectedItems),
      inputMethod: method,
    };

    onSubmit({
      type: getTemplateCreateType(resolvedOrderType, isNomenclatureV3),
      originalTemplate,
      templateDto: fullDto,
      selectedItems,
    })
      .tap(onClose)
      .tap(setSelectedTemplate)
      .catch((error) => {
        setError(getTemplateCreationErrorMessage(error));
      });
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(_onSubmit)(e);
  };

  return (
    <>
      <CustomDialog
        onClose={onClose}
        open={open}
        title={title}
        size={"md"}
        heigth={"80vh"}
      >
        <form onSubmit={onFormSubmit} autoComplete="off">
          <Stack spacing="1rem" p="1rem">
            {/* TODO eventually add the instructional videos <InstructionalVideos type={type} /> */}
            <TextField
              control={control}
              name="name"
              label="Nombre de plantilla"
              variant="outlined"
              required
              helperText={<NameHelperText />}
              style={{ width: "100%" }}
            />
            <RadioGroup
              method={method}
              handleChange={(event) => {
                setMethod(() => event.target.value);
              }}
              options={templateTypes}
            />
            <NomenclatureSearch
              onSelect={addItem}
              procedureType={isPractices ? (lockedProcedureType ?? OrderType.PRACTICES) : orderType}
              searchOptions={{
                searchNomenclature: true,
                searchTemplates: false,
              }}
              templates={[]}
              minQueryLength={3}
              disabled={hasReachedMaxItems(maxItems, selectedItems)}
              searchPlaceholder={isPractices ? "Seleccioná una práctica" : undefined}
              showTypeChips={isPractices ? true : undefined}
              isNomenclatureV3={isNomenclatureV3}
            />
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {selectedItems.map((item) => (
                <NomenclatureOrderItem
                  key={itemKey(item)}
                  item={item}
                  onRemove={() => onItemRemove(item)}
                  showRemoveButton={true}
                />
              ))}
            </Stack>
            <Button
              variant="contained"
              type="submit"
              disabled={selectedItems.length === 0}
            >
              {buttonText}
            </Button>
          </Stack>
        </form>
        <ErrorAlert
          show={!_.isEmpty(error)}
          onClose={() => setError(null)}
          message={error}
        />
      </CustomDialog>
    </>
  );
};

export default TemplateDialog;
